import axios from 'axios';

import type { AxiosHeaders, AxiosRequestConfig, AxiosResponse } from 'axios';

declare const AXIOS_BASE: string;
declare const AXIOS_HEADERS: string;

export type RequestConfig<TData = unknown> = {
  url?: string;
  method: 'GET' | 'PUT' | 'PATCH' | 'POST' | 'DELETE' | 'OPTIONS';
  params?: unknown;
  data?: TData | FormData;
  responseType?: 'arraybuffer' | 'blob' | 'document' | 'json' | 'text' | 'stream';
  signal?: AbortSignal;
  headers?: AxiosRequestConfig['headers'];
};

export type ResponseConfig<TData = unknown> = {
  data: TData;
  status: number;
  statusText: string;
  headers?: AxiosResponse['headers'];
};

export type ResponseErrorConfig<TError = unknown> = TError;

/**
 * 从cookie中获取认证token
 * @returns 认证token，如果不存在则返回null
 */
export const getAuthTokenFromCookie = (): string | null => {
  // 在浏览器环境中获取cookie
  if (typeof document !== 'undefined') {
    const cookies = document.cookie.split(';');
    for (const cookie of cookies) {
      const [name, value] = cookie.trim().split('=');
      if (name === 'ide_auth_token') {
        return value;
      }
    }
  }
  return null;
};

/**
 * 删除认证cookie
 * 通过设置过期时间为过去的时间来删除cookie
 */
export const removeAuthTokenFromCookie = (): void => {
  if (typeof document !== 'undefined') {
    // 设置cookie的过期时间为过去的时间，从而删除cookie
    document.cookie = 'ide_auth_token=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
  }
};

export const client = axios.create({
  baseURL: typeof AXIOS_BASE !== 'undefined' ? AXIOS_BASE : undefined,
  headers:
    typeof AXIOS_HEADERS !== 'undefined' ? (JSON.parse(AXIOS_HEADERS) as AxiosHeaders) : undefined,
});

// 请求拦截器：自动添加认证token到请求头
client.interceptors.request.use(
  (config) => {
    // 设置 accept-language 为英文，确保接口返回英文内容
    config.headers = config.headers || {};
    config.headers['accept-language'] = 'en-US,en;q=0.9';
    
    const token = getAuthTokenFromCookie();
    if (token) {
      // 如果请求头中没有Authorization，则添加Bearer token
      if (!config.headers?.Authorization) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);


client.interceptors.response.use(
  (response) => {
    // 检查响应头中是否有新的token
    const newToken = response.headers['x-new-token'];
    if (newToken && typeof document !== 'undefined') {
      // 更新cookie中的token
      const tokenValue = newToken.replace('Bearer ', '');
      document.cookie = `ide_auth_token=${tokenValue}; path=/; max-age=${7 * 24 * 60 * 60}`; // 7天过期
    }
    
    return response.data;
  },
  (error) => {
    // 如果是401错误，清除认证cookie并跳转到登录页
    if (error.response?.status === 401) {
      removeAuthTokenFromCookie();
      // 只有在不是登录相关页面时才跳转
      if (typeof window !== 'undefined' && 
          !window.location.pathname.includes('/login') && 
          !window.location.pathname.includes('/register') &&
          !window.location.pathname.includes('/forgot-password')) {
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);
/**
 * 使用示例：
 * 
 * // 获取用户信息 - 会自动从cookie获取token并添加到请求头
 * const getUserInfo = async () => {
 *   const response = await client.get('/api/user/me');
 *   return response.data;
 * };
 * 
 * // 发送带认证的POST请求
 * const createResource = async (data: any) => {
 *   const response = await client.post('/api/resource', data);
 *   return response.data;
 * };
 */